import type { DefaultValues, FieldPath, FieldValues } from "react-hook-form";
import type { ZodSchema } from "zod";
import {
  offerContactFormConfig,
  offerContactFormDefaultValues,
  vehicleContactFormConfig,
  vehicleContactFormDefaultValues,
  type OfferContactFormFieldNameT,
} from "@/app/components/forms/contact/offer-contact-form-config";
import {
  camperContactFormConfig,
  camperContactFormDefaultValues,
  type CamperContactFormFieldNameT,
} from "@/app/components/forms/contact/camper-contact-form-config";
import {
  serviceContactFormConfig,
  serviceContactFormDefaultValues,
  type ServiceContactFormFieldNameT,
} from "@/app/components/forms/contact/service-contact-form-config";
import {
  campaignContactFormConfig,
  campaignContactFormDefaultValues,
  type CampaignContactFormFieldNameT,
} from "@/app/components/forms/contact/campaign-contact-form-config";
import {
  standardContactFormConfig,
  standardContactFormDefaultValues,
  type StandardContactFormFieldNameT,
} from "@/app/components/forms/contact/standard-contact-form-config";
import {
  postContactFormConfig,
  postContactFormDefaultValues,
  type PostContactFormFieldNameT,
} from "@/app/components/forms/contact/post-contact-form-config";
import {
  companyContactFormConfig,
  companyContactFormDefaultValues,
  type CompanyContactFormFieldNameT,
} from "@/app/components/forms/contact/company-contact-form-config";
import {
  companyOfferContactFormConfig,
  companyOfferContactFormDefaultValues,
  type CompanyOfferContactFormFieldNameT,
} from "@/app/components/forms/contact/company-offer-contact-form-config";
import {
  jobContactFormConfig,
  jobContactFormDefaultValues,
  type JobContactFormFieldNameT,
} from "@/app/components/forms/contact/job-contact-form-config";
import {
  giveawayContactFormConfig,
  giveawayContactFormDefaultValues,
  type GiveawayContactFormFieldNameT,
} from "@/app/components/forms/contact/charity-giveaway-contact-form-config";
import {
  leasingvertragContactFormConfig,
  leasingvertragContactFormDefaultValues,
  type LeasingvertragContactFormFieldNameT,
} from "@/app/components/forms/contact/leasingvertrag-contact-form-config";
import {
  offerContactFormSchema,
  camperContactFormSchema,
  serviceContactFormSchema,
  campaignContactFormSchema,
  standardContactFormSchema,
  postContactFormSchema,
  companyContactFormSchema,
  jobContactFormSchema,
  giveawayContactFormSchema,
  leasingvertragContactFormSchema,
  type OfferContactFormDataT,
  type CamperContactFormDataT,
  type ServiceContactFormDataT,
  type CampaignContactFormDataT,
  type StandardContactFormDataT,
  type PostContactFormDataT,
  type CompanyContactFormDataT,
  type JobContactFormDataT,
  type GiveawayContactFormDataT,
  type LeasingvertragContactFormDataT,
} from "@/app/schemas/zod/contact-form-schema";
import type { ContactFormConfigT } from "./contact-form-types";
import type { CompanyPositionT } from "@/app/types/companies";

type SelectedOptionMetaT = {
  id: string;
  name: string;
};

export type SelectedSelectMetaT = {
  location?: SelectedOptionMetaT;
  make?: SelectedOptionMetaT;
  makeName?: SelectedOptionMetaT;
  contactPerson?: SelectedOptionMetaT;
};

type SubmissionPayloadMapper<
  TFormValues extends FieldValues,
  TContext,
  TContextRequired extends boolean,
> = (params: {
  data: Omit<TFormValues, "mail">;
  context: TContextRequired extends true ? TContext : TContext | undefined;
  selectMeta?: SelectedSelectMetaT;
}) => Record<string, unknown>;

type VariantTypeBagShape<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
  TContext,
  TContextRequired extends boolean,
> = {
  formValues: TFormValues;
  fieldName: TFieldName;
  selectFieldName: TSelectFieldName;
  context: TContext;
  contextRequired: TContextRequired;
};

export type ContactFormVariantDefinition<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
  TContext,
  TContextRequired extends boolean,
> = {
  schema: ZodSchema<TFormValues>;
  defaultValues: DefaultValues<TFormValues>;
  config: ContactFormConfigT<TFieldName>;
  selectFieldNames: readonly TSelectFieldName[];
  contextRequired: TContextRequired;
  mapSubmissionPayload: SubmissionPayloadMapper<
    TFormValues,
    TContext,
    TContextRequired
  >;
  ui: {
    defaultPresentation: "card" | "plain";
  };
  __types__: VariantTypeBagShape<
    TFormValues,
    TFieldName,
    TSelectFieldName,
    TContext,
    TContextRequired
  >;
};

type ContactFormVariantDefinitionInput<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
  TContext,
  TContextRequired extends boolean,
> = Omit<
  ContactFormVariantDefinition<
    TFormValues,
    TFieldName,
    TSelectFieldName,
    TContext,
    TContextRequired
  >,
  "__types__"
>;

function defineContactFormVariant<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
  TContext,
  TContextRequired extends boolean,
>(
  definition: ContactFormVariantDefinitionInput<
    TFormValues,
    TFieldName,
    TSelectFieldName,
    TContext,
    TContextRequired
  >
): ContactFormVariantDefinition<
  TFormValues,
  TFieldName,
  TSelectFieldName,
  TContext,
  TContextRequired
> {
  return {
    ...definition,
    __types__: undefined as unknown as VariantTypeBagShape<
      TFormValues,
      TFieldName,
      TSelectFieldName,
      TContext,
      TContextRequired
    >,
  };
}

type OfferContactFormContextT = {
  offer?: {
    id: string;
    title: string | null;
    make: string | null;
    model: string | null;
    aktion?: string | null;
    vehicleCondition?: string | null;
    location?: string | null;
    locationId?: string | null;
  };
};

type CamperContactFormContextT = undefined;

export type ServiceContactFormContextT = {
  serviceId: string;
  serviceTitle: string;
  serviceSlug: string;
};

export type CampaignContactFormContextT = {
  campaignId: string;
  campaignTitle: string;
  campaignSlug: string;
};

export type StandardContactFormContextT = {
  slug: string;
};

export type PostContactFormContextT = {
  postId: string;
  postTitle: string;
  postSlug: string;
  categoryLabels?: string[];
};

export type CompanyContactFormContextT = {
  companyId: string;
  companyName: string;
  slug: string;
  position?: CompanyPositionT | null;
  vehicleCondition?: string | null;
  offer?: {
    id: string;
    title?: string | null;
    make?: string | null;
    model?: string | null;
    aktion?: string | null;
    vehicleCondition?: string | null;
    location?: string | null;
    locationId?: string | null;
  } | null;
};

export type JobContactFormContextT = {
  jobTitle: string;
  jobSlug: string;
  employmentType: string;
  employmentLabel?: string | null;
  locationName?: string | null;
  locationId?: string | null;
};

const offerSelectFieldNames = ["salutation", "location", "makeName"] as const;
const camperSelectFieldNames = ["location", "make", "salutation"] as const;
const serviceSelectFieldNames = ["salutation", "location", "make"] as const;
const campaignSelectFieldNames = ["location", "salutation"] as const;
const standardSelectFieldNames = ["salutation"] as const;
const postSelectFieldNames = ["salutation", "location"] as const;
const companySelectFieldNames = ["salutation"] as const;
const companyOfferSelectFieldNames = ["salutation"] as const;
const jobSelectFieldNames = ["salutation"] as const;
const giveawaySelectFieldNames = ["salutation"] as const;
const leasingvertragSelectFieldNames = [
  "salutation",
  "make",
  "contactPerson",
] as const;

const normalizeOptionalString = (
  value: string | null | undefined
): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
};

const resolveSubmissionLocation = (params: {
  fieldValue?: string | null;
  selectMeta?: SelectedOptionMetaT;
  fallbackName?: string | null;
  fallbackId?: string | null;
}): {
  location: string | null;
  locationId: string | null;
} => {
  const normalizedFieldValue = normalizeOptionalString(params.fieldValue);
  const normalizedSelectName = normalizeOptionalString(params.selectMeta?.name);
  const normalizedFallbackName = normalizeOptionalString(params.fallbackName);

  const resolvedLocationName =
    normalizedSelectName &&
    (!normalizedFieldValue || normalizedSelectName !== normalizedFieldValue)
      ? normalizedSelectName
      : (normalizedFallbackName ??
        normalizedSelectName ??
        normalizedFieldValue);

  return {
    location: resolvedLocationName,
    locationId:
      normalizeOptionalString(params.selectMeta?.id) ??
      normalizeOptionalString(params.fallbackId) ??
      normalizedFieldValue,
  };
};

const mapOfferPayload = (
  data: Omit<OfferContactFormDataT, "mail">,
  context: OfferContactFormContextT | undefined,
  selectMeta?: SelectedSelectMetaT
) => {
  const resolvedLocation = resolveSubmissionLocation({
    fieldValue: data.location,
    selectMeta: selectMeta?.location,
    fallbackName: context?.offer?.location,
    fallbackId: context?.offer?.locationId,
  });

  return {
    company: data.company || null,
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone,
    comments: data.comments || null,
    location: resolvedLocation.location,
    locationId: resolvedLocation.locationId,
    make: data.make || context?.offer?.make || null,
    aktion: data.aktion || context?.offer?.aktion || null,
    vehicleCondition:
      data.vehicleCondition || context?.offer?.vehicleCondition || null,
    desiredFinancing: data.desiredFinancing || null,
    offer: context?.offer
      ? {
          id: context.offer.id,
          title: context.offer.title || null,
          make: context.offer.make || null,
          model: context.offer.model || null,
        }
      : null,
    vehicle: {
      makeName: (selectMeta?.makeName?.name ?? data.makeName) || null,
      licensePlate: data.vehicleLicensePlate || null,
      vin: data.vehicleVin || null,
      mileage: data.vehicleMileage || null,
      model: data.vehicleModel || null,
    },
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: data.source,
    dealerdesk: data.dealerdesk,
  };
};

const mapCamperPayload = (
  data: Omit<CamperContactFormDataT, "mail">,
  selectMeta?: SelectedSelectMetaT
) => {
  const resolvedLocation = resolveSubmissionLocation({
    fieldValue: data.location,
    selectMeta: selectMeta?.location,
  });

  return {
    company: data.company || null,
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone,
    comments: data.comments || null,
    location: resolvedLocation.location,
    locationId: resolvedLocation.locationId,
    make: data.make,
    makeName: selectMeta?.make?.name ?? null,
    vehicle: {
      makeName: selectMeta?.make?.name ?? data.make ?? null,
      licensePlate: data.vehicleLicensePlate || null,
      vin: data.vehicleVin || null,
      mileage: data.vehicleMileage || null,
      model: data.vehicleModel || null,
    },
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: data.source,
    dealerdesk: data.dealerdesk,
  };
};

const mapServicePayload = (
  data: Omit<ServiceContactFormDataT, "mail">,
  context: ServiceContactFormContextT | undefined,
  selectMeta?: SelectedSelectMetaT
) => {
  const resolvedMake = (selectMeta?.make?.name ?? data.make) || null;
  const resolvedLocation = resolveSubmissionLocation({
    fieldValue: data.location,
    selectMeta: selectMeta?.location,
  });
  const servicePayload = context
    ? {
        id: context.serviceId,
        title: context.serviceTitle,
        slug: context.serviceSlug,
        make: resolvedMake,
      }
    : resolvedMake
      ? {
          id: null,
          title: null,
          slug: null,
          make: resolvedMake,
        }
      : null;

  return {
    company: data.company || null,
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone,
    comments: data.comments || null,
    location: resolvedLocation.location,
    locationId: resolvedLocation.locationId,
    service: servicePayload,
    vehicle: {
      makeName: resolvedMake,
      licensePlate: data.vehicleLicensePlate || null,
      vin: data.vehicleVin || null,
      mileage: data.vehicleMileage || null,
      model: data.vehicleModel || null,
    },
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: data.source,
    dealerdesk: data.dealerdesk,
  };
};

const mapCampaignPayload = (
  data: Omit<CampaignContactFormDataT, "mail">,
  context: CampaignContactFormContextT,
  selectMeta?: SelectedSelectMetaT
) => {
  const resolvedLocation = resolveSubmissionLocation({
    fieldValue: data.location,
    selectMeta: selectMeta?.location,
  });

  return {
    company: data.company || null,
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone,
    comments: data.comments || null,
    location: resolvedLocation.location,
    locationId: resolvedLocation.locationId,
    desiredFinancing: data.desiredFinancing || null,
    campaign: {
      id: context.campaignId,
      title: context.campaignTitle,
      slug: context.campaignSlug,
    },
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: data.source,
    dealerdesk: data.dealerdesk,
  };
};

const mapStandardPayload = (
  data: Omit<StandardContactFormDataT, "mail">,
  context: StandardContactFormContextT
) => ({
  company: data.company || null,
  salutation: data.salutation || null,
  firstname: data.firstname,
  lastname: data.lastname,
  email: data.email,
  phone: data.phone,
  comments: data.comments || null,
  slug: context.slug,
  privacy: data.privacy,
  case: data.case,
  URL: data.URL || null,
  source: data.source,
  dealerdesk: data.dealerdesk,
});

const mapPostPayload = (
  data: Omit<PostContactFormDataT, "mail">,
  context: PostContactFormContextT,
  selectMeta?: SelectedSelectMetaT
) => {
  const resolvedLocation = resolveSubmissionLocation({
    fieldValue: data.location,
    selectMeta: selectMeta?.location,
  });

  return {
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone || null,
    comments: data.comments || null,
    location: resolvedLocation.location,
    locationId: resolvedLocation.locationId,
    post: {
      id: context.postId,
      title: context.postTitle,
      slug: context.postSlug,
      categories: context.categoryLabels ?? [],
    },
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: data.source,
    dealerdesk: data.dealerdesk,
  };
};

const mapCompanyPayload = (
  data: Omit<CompanyContactFormDataT, "mail">,
  context: CompanyContactFormContextT
) => {
  const isLargeCustomer = context.position === "grosskunden";

  return {
    salutation: data.salutation,
    firstname: data.firstname,
    lastname: data.lastname,
    email: data.email,
    phone: data.phone,
    comments: data.comments || null,
    company: data.company || context.companyName || null,
    companyDetails: {
      id: context.companyId,
      name: context.companyName,
      slug: context.slug,
      position: context.position || null,
    },
    offer: context.offer
      ? {
          id: context.offer.id,
          title: context.offer.title || null,
          make: context.offer.make || null,
          model: context.offer.model || null,
        }
      : null,
    privacy: data.privacy,
    case: data.case,
    URL: data.URL || null,
    source: isLargeCustomer ? "Firmen Anfrage Grosskunden" : data.source,
    dealerdesk: isLargeCustomer ? "Firmenangebot Grosskunden" : data.dealerdesk,
    vehicleCondition: data.vehicleCondition || context.vehicleCondition || null,
    abrufschein: typeof data.abrufschein === "string" ? data.abrufschein : null,
  };
};

const mapVehiclePayload = (
  data: Omit<OfferContactFormDataT, "mail">,
  context: OfferContactFormContextT | undefined,
  selectMeta?: SelectedSelectMetaT
) => {
  const basePayload = mapOfferPayload(data, context, selectMeta);
  return {
    ...basePayload,
    source: "Gebrauchtwagen Anfrage",
    dealerdesk: "Gebrauchtwagen",
  };
};

const mapJobPayload = (
  data: Omit<JobContactFormDataT, "mail">,
  context: JobContactFormContextT
) => ({
  salutation: data.salutation,
  firstname: data.firstname,
  lastname: data.lastname,
  email: data.email,
  phone: data.phone || null,
  comments: data.comments || null,
  privacy: data.privacy,
  case: data.case,
  URL: data.URL || null,
  source: data.source,
  dealerdesk: data.dealerdesk,
  location: context.locationName || null,
  locationId: context.locationId || null,
  zipCode: data.zipCode,
  city: data.city,
  cv: typeof data.cv === "string" ? data.cv : null,
  certificates:
    typeof data.certificates === "string" ? data.certificates : null,
  job: {
    title: context.jobTitle,
    slug: context.jobSlug,
    employmentType: context.employmentType,
    employmentLabel: context.employmentLabel || null,
    location: context.locationName || null,
    locationId: context.locationId || null,
  },
});

const mapLeasingvertragPayload = (
  data: Omit<LeasingvertragContactFormDataT, "mail">,
  selectMeta?: SelectedSelectMetaT
) => ({
  company: data.company || null,
  salutation: data.salutation,
  firstname: data.firstname,
  lastname: data.lastname,
  email: data.email,
  phone: data.phone,
  comments: data.comments || null,
  licensePlate: data.licensePlate,
  make: (selectMeta?.make?.name ?? data.make) || null,
  contactPerson: data.contactPerson
    ? {
        id: data.contactPerson,
        name: selectMeta?.contactPerson?.name ?? data.contactPerson,
      }
    : null,
  privacy: data.privacy,
  case: data.case,
  URL: data.URL || null,
  source: data.source,
  dealerdesk: data.dealerdesk,
});

export const CONTACT_FORM_VARIANTS = {
  offer: defineContactFormVariant<
    OfferContactFormDataT,
    OfferContactFormFieldNameT,
    (typeof offerSelectFieldNames)[number],
    OfferContactFormContextT,
    false
  >({
    schema: offerContactFormSchema,
    defaultValues: offerContactFormDefaultValues,
    config: offerContactFormConfig,
    selectFieldNames: offerSelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data, context, selectMeta }) =>
      mapOfferPayload(
        data,
        context as OfferContactFormContextT | undefined,
        selectMeta
      ),
    ui: {
      defaultPresentation: "card",
    },
  }),
  vehicle: defineContactFormVariant<
    OfferContactFormDataT,
    OfferContactFormFieldNameT,
    (typeof offerSelectFieldNames)[number],
    OfferContactFormContextT,
    false
  >({
    schema: offerContactFormSchema,
    defaultValues: vehicleContactFormDefaultValues,
    config: vehicleContactFormConfig,
    selectFieldNames: offerSelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data, context, selectMeta }) =>
      mapVehiclePayload(
        data,
        context as OfferContactFormContextT | undefined,
        selectMeta
      ),
    ui: {
      defaultPresentation: "card",
    },
  }),
  camper: defineContactFormVariant<
    CamperContactFormDataT,
    CamperContactFormFieldNameT,
    (typeof camperSelectFieldNames)[number],
    CamperContactFormContextT,
    false
  >({
    schema: camperContactFormSchema,
    defaultValues: camperContactFormDefaultValues,
    config: camperContactFormConfig,
    selectFieldNames: camperSelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data, selectMeta }) =>
      mapCamperPayload(data, selectMeta),
    ui: {
      defaultPresentation: "card",
    },
  }),
  service: defineContactFormVariant<
    ServiceContactFormDataT,
    ServiceContactFormFieldNameT,
    (typeof serviceSelectFieldNames)[number],
    ServiceContactFormContextT,
    false
  >({
    schema: serviceContactFormSchema,
    defaultValues: serviceContactFormDefaultValues,
    config: serviceContactFormConfig,
    selectFieldNames: serviceSelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data, context, selectMeta }) =>
      mapServicePayload(
        data,
        context as ServiceContactFormContextT | undefined,
        selectMeta
      ),
    ui: {
      defaultPresentation: "card",
    },
  }),
  campaign: defineContactFormVariant<
    CampaignContactFormDataT,
    CampaignContactFormFieldNameT,
    (typeof campaignSelectFieldNames)[number],
    CampaignContactFormContextT,
    true
  >({
    schema: campaignContactFormSchema,
    defaultValues: campaignContactFormDefaultValues,
    config: campaignContactFormConfig,
    selectFieldNames: campaignSelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context, selectMeta }) =>
      mapCampaignPayload(
        data,
        context as CampaignContactFormContextT,
        selectMeta
      ),
    ui: {
      defaultPresentation: "card",
    },
  }),
  standard: defineContactFormVariant<
    StandardContactFormDataT,
    StandardContactFormFieldNameT,
    (typeof standardSelectFieldNames)[number],
    StandardContactFormContextT,
    true
  >({
    schema: standardContactFormSchema,
    defaultValues: standardContactFormDefaultValues,
    config: standardContactFormConfig,
    selectFieldNames: standardSelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context }) =>
      mapStandardPayload(data, context as StandardContactFormContextT),
    ui: {
      defaultPresentation: "card",
    },
  }),
  post: defineContactFormVariant<
    PostContactFormDataT,
    PostContactFormFieldNameT,
    (typeof postSelectFieldNames)[number],
    PostContactFormContextT,
    true
  >({
    schema: postContactFormSchema,
    defaultValues: postContactFormDefaultValues,
    config: postContactFormConfig,
    selectFieldNames: postSelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context, selectMeta }) =>
      mapPostPayload(data, context as PostContactFormContextT, selectMeta),
    ui: {
      defaultPresentation: "card",
    },
  }),
  company: defineContactFormVariant<
    CompanyContactFormDataT,
    CompanyContactFormFieldNameT,
    (typeof companySelectFieldNames)[number],
    CompanyContactFormContextT,
    true
  >({
    schema: companyContactFormSchema,
    defaultValues: companyContactFormDefaultValues,
    config: companyContactFormConfig,
    selectFieldNames: companySelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context }) =>
      mapCompanyPayload(data, context as CompanyContactFormContextT),
    ui: {
      defaultPresentation: "card",
    },
  }),
  companyOffer: defineContactFormVariant<
    CompanyContactFormDataT,
    CompanyOfferContactFormFieldNameT,
    (typeof companyOfferSelectFieldNames)[number],
    CompanyContactFormContextT,
    true
  >({
    schema: companyContactFormSchema,
    defaultValues: companyOfferContactFormDefaultValues,
    config: companyOfferContactFormConfig,
    selectFieldNames: companyOfferSelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context }) =>
      mapCompanyPayload(data, context as CompanyContactFormContextT),
    ui: {
      defaultPresentation: "card",
    },
  }),
  job: defineContactFormVariant<
    JobContactFormDataT,
    JobContactFormFieldNameT,
    (typeof jobSelectFieldNames)[number],
    JobContactFormContextT,
    true
  >({
    schema: jobContactFormSchema,
    defaultValues: jobContactFormDefaultValues,
    config: jobContactFormConfig,
    selectFieldNames: jobSelectFieldNames,
    contextRequired: true,
    mapSubmissionPayload: ({ data, context }) =>
      mapJobPayload(data, context as JobContactFormContextT),
    ui: {
      defaultPresentation: "plain",
    },
  }),
  giveaway: defineContactFormVariant<
    GiveawayContactFormDataT,
    GiveawayContactFormFieldNameT,
    (typeof giveawaySelectFieldNames)[number],
    undefined,
    false
  >({
    schema: giveawayContactFormSchema,
    defaultValues: giveawayContactFormDefaultValues,
    config: giveawayContactFormConfig,
    selectFieldNames: giveawaySelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data }) => ({
      salutation: data.salutation,
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      phone: data.phone || null,
      privacy: data.privacy,
      case: data.case,
      URL: data.URL || null,
      source: data.source,
      dealerdesk: data.dealerdesk,
    }),
    ui: {
      defaultPresentation: "card",
    },
  }),
  leasingvertrag: defineContactFormVariant<
    LeasingvertragContactFormDataT,
    LeasingvertragContactFormFieldNameT,
    (typeof leasingvertragSelectFieldNames)[number],
    undefined,
    false
  >({
    schema: leasingvertragContactFormSchema,
    defaultValues: leasingvertragContactFormDefaultValues,
    config: leasingvertragContactFormConfig,
    selectFieldNames: leasingvertragSelectFieldNames,
    contextRequired: false,
    mapSubmissionPayload: ({ data, selectMeta }) =>
      mapLeasingvertragPayload(data, selectMeta),
    ui: {
      defaultPresentation: "card",
    },
  }),
} as const;

export type ContactFormVariantRegistryT = typeof CONTACT_FORM_VARIANTS;

export type ContactFormVariantKeyT = keyof ContactFormVariantRegistryT;

type ContactFormVariantDefinitionForInternal<
  TVariant extends ContactFormVariantKeyT,
> = ContactFormVariantRegistryT[TVariant];

export type ContactFormVariantDefinitionFor<
  TVariant extends ContactFormVariantKeyT,
> = ContactFormVariantDefinitionForInternal<TVariant>;

type VariantTypeBag<TVariant extends ContactFormVariantKeyT> =
  ContactFormVariantDefinitionFor<TVariant>["__types__"];

export type ContactFormVariantFieldNameT<
  TVariant extends ContactFormVariantKeyT,
> = FieldPath<ContactFormVariantFormDataT<TVariant>>;

export type ContactFormVariantFormDataT<
  TVariant extends ContactFormVariantKeyT,
> = VariantTypeBag<TVariant>["formValues"];

export type ContactFormVariantSelectFieldNameT<
  TVariant extends ContactFormVariantKeyT,
> = Extract<
  ContactFormVariantFieldNameT<TVariant>,
  VariantTypeBag<TVariant>["selectFieldName"]
>;

export type ContactFormVariantContextT<
  TVariant extends ContactFormVariantKeyT,
> = VariantTypeBag<TVariant>["context"];

export type ContactFormVariantContextRequirementT<
  TVariant extends ContactFormVariantKeyT,
> = VariantTypeBag<TVariant>["contextRequired"];

export type ContactFormVariantSubmissionArgsT<
  TVariant extends ContactFormVariantKeyT,
> = Parameters<
  ContactFormVariantDefinitionFor<TVariant>["mapSubmissionPayload"]
>[0];

export function getContactFormVariantDefinition<
  TVariant extends ContactFormVariantKeyT,
>(variant: TVariant): ContactFormVariantDefinitionFor<TVariant> {
  return CONTACT_FORM_VARIANTS[variant];
}
